import { EzBadParamException } from '/ezlibrary/exceptions/EzBadParamException.js';

import { EzStaticClass } from '/ezlibrary/EzStaticClass.js';

import { EzObject } from '/ezlibrary/helpers/EzObject.js';

/*
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ENGINEERING NOTES
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
        1) Do not import anything from /ezlibrary/helpers/EzHelpers.js into this module
        2) Do not import the following into this module:
            import { EzApi } from '/public/common/javascript/ezapi.js';
            import { EzUI } from '/public/common/javascript/ezui.js';
            import { ezUI } from '/public/common/javascript/ezui.js';
            import { EzFloat } from '/ezlibrary/helpers/EzFloat.js';
            import { EzString } from '/ezlibrary/helpers/EzString.js';
            import { EzArray } from '/ezlibrary/helpers/EzArray.js';
            import { EzHtml } from '/ezlibrary/helpers/EzHtml.js';
            import { EzFunction } from '/ezlibrary/helpers/EzFunction.js';
            import { EzJson } from '/ezlibrary/helpers/EzJson.js';
            import { EzConsole } from '/ezlibrary/helpers/EzConsole.js';
            import { EzAsync } from '/ezlibrary/helpers/EzAsync.js';
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
*/

/**
 * @class
 * @extends {EzStaticClass}
 * @description
 * Static class that provides utility methods and properties for numbers
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into non-helper classes with:
 *      import {
 *          // ... other helper class imports
 *          EzNumber
 *      } from '/ezlibrary/helpers/EzHelpers.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into other helper classes with:
 *     import { EzNumber } from '/ezlibrary/helpers/EzNumber.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Static Access Only:
 *     EzNumber.{property or method}
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzNumber extends EzStaticClass {
    /*
        ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
        ENGINEERING NOTES
        ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
        1) Provide ONLY static fields, properties, and/or methods in this class.
        2) Do not create instances of this class!
        ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    */

   /**
     * @static
     * @public @readonly @property
     * Gets string constant: '-1'
     * @returns {string}
     */
    static get sNEG_ONE() {
        return '-1';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets string constant: '0'
     * @returns {string}
     */
    static get s0() {
        return '0';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets string constant: '1'
     * @returns {string}
     */
    static get s1() {
        return '1';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets string constant: '2'
     * @returns {string}
     */
    static get s2() {
        return '2';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets string constant: '3'
     * @returns {string}
     */
    static get s3() {
        return '3';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets string constant: '4'
     * @returns {string}
     */
    static get s4() {
        return '4';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets string constant: '5'
     * @returns {string}
     */
    static get s5() {
        return '5';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets string constant: '6'
     * @returns {nustringmber}
     */
    static get s6() {
        return '6';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets string constant: '7'
     * @returns {string}
     */
    static get s7() {
        return '7';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets string constant: '8'
     * @returns {string}
     */
    static get s8() {
        return '8';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets string constant: '9'
     * @returns {string}
     */
    static get s9() {
        return '9';
    }


    /**
     * @static
     * @public @readonly @property
     * Gets the number -13.
     * @returns {number}
     */
    static get MINUS_13() {
        return EzNumber._n13;
    }
    /**
     * @static
     * @public @readonly @property
     * Gets the number -13.
     * @returns {number}
     */
    static get _n13() {
        return -13;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number -6.
     * @returns {number}
     */
    static get MINUS_6() {
        return EzNumber._n6;
    }
    /**
     * @static
     * @public @readonly @property
     * Gets the number -6.
     * @returns {number}
     */
    static get _n6() {
        return -6;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number -1.
     * @returns {number}
     */
    static get MINUS_ONE() {
        return EzNumber._n1;
    }
    /**
     * @static
     * @public @readonly @property
     * Gets the number -1.
     * @returns {number}
     */
    static get _n1() {
        return -1;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 0.
     * @returns {number}
     */
    static get ZERO() {
        return EzNumber._0;
    }
    /**
     * @static
     * @public @readonly @property
     * Gets the number 0.
     * @returns {number}
     */
    static get _0() {
        return 0;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 1.
     * @returns {number}
     */
    static get ONE() {
        return EzNumber._1;
    }
    /**
     * @static
     * @public @readonly @property
     * Gets the number 1.
     * @returns {number}
     */
    static get _1() {
        return 1;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 2.
     * @returns {number}
     */
    static get TWO() {
        return EzNumber._2;
    }
    /**
     * @static
     * @public @readonly @property
     * Gets the number 2.
     * @returns {number}
     */
    static get _2() {
        return 2;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 3.
     * @returns {number}
     */
    static get THREE() {
        return EzNumber._3;
    }
    /**
     * @static
     * @public @readonly @property
     * Gets the number 3.
     * @returns {number}
     */
    static get _3() {
        return 3;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 4.
     * @returns {number}
     */
    static get FOUR() {
        return EzNumber._4;
    }
    /**
     * @static
     * @public @readonly @property
     * Gets the number 4.
     * @returns {number}
     */
    static get _4() {
        return 4;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 5.
     * @returns {number}
     */
    static get FIVE() {
        return EzNumber._5;
    }
    /**
     * @static
     * @public @readonly @property
     * Gets the number 5.
     * @returns {number}
     */
    static get _5() {
        return 5;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 6.
     * @returns {number}
     */
    static get SIX() {
        return EzNumber._6;
    }
    /**
     * @static
     * @public @readonly @property
     * Gets the number 6.
     * @returns {number}
     */
    static get _6() {
        return 6;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 7.
     * @returns {number}
     */
    static get SEVEN() {
        return EzNumber._7;
    }
    /**
     * @static
     * @public @readonly @property
     * Gets the number 7.
     * @returns {number}
     */
    static get _7() {
        return 7;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 8.
     * @returns {number}
     */
    static get EIGHT() {
        return EzNumber._8;
    }
    /**
     * @static
     * @public @readonly @property
     * Gets the number 8.
     * @returns {number}
     */
    static get _8() {
        return 8;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 9.
     * @returns {number}
     */
    static get NINE() {
        return EzNumber._9;
    }
    /**
     * @static
     * @public @readonly @property
     * Gets the number 9.
     * @returns {number}
     */
    static get _9() {
        return 9;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 10.
     * @returns {number}
     */
    static get TEN() {
        return EzNumber._10;
    }
    /**
     * @static
     * @public @readonly @property
     * Gets the number 10.
     * @returns {number}
     */
    static get _10() {
        return 10;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 12.
     * @returns {number}
     */
    static get TWELEVE() {
        return EzNumber._12;
    }
    /**
     * @static
     * @public @readonly @property
     * Gets the number 12.
     * @returns {number}
     */
    static get _12() {
        return 12;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 12.
     * @returns {number}
     */
    static get THIRTEEN() {
        return EzNumber._13;
    }
    /**
     * @static
     * @public @readonly @property
     * Gets the number 13.
     * @returns {number}
     */
    static get _13() {
        return 13;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 15.
     * @returns {number}
     */
    static get FIFTEEN() {
        return EzNumber._15;
    }
    /**
     * @static
     * @public @readonly @property
     * Gets the number 15.
     * @returns {number}
     */
    static get _15() {
        return 15;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 17.
     * @returns {number}
     */
    static get SEVENTEEN() {
        return EzNumber._17;
    }
    /**
     * @static
     * @public @readonly @property
     * Gets the number 17.
     * @returns {number}
     */
    static get _17() {
        return 17;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 18.
     * @returns {number}
     */
    static get _18() {
        return 18;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 19
     * @returns {number}
     */
    static get _19() {
        return 19;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 23.
     * @returns {number}
     */
    static get TWENTY_THREE() {
        return EzNumber._23;
    }
    /**
     * @static
     * @public @readonly @property
     * Gets the number 23.
     * @returns {number}
     */
    static get _23() {
        return 23;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 24.
     * @returns {number}
     */
    static get TWENTY_FOUR() {
        return EzNumber._24;
    }
    /**
     * @static
     * @public @readonly @property
     * Gets the number 24.
     * @returns {number}
     */
    static get _24() {
        return 24;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 27.
     * @returns {number}
     */
    static get _27() {
        return 24;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 30.
     * @returns {number}
     */
    static get THIRTY() {
        return EzNumber._30;
    }
    /**
     * @static
     * @public @readonly @property
     * Gets the number 30.
     * @returns {number}
     */
    static get _30() {
        return 30;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 30.
     * @returns {number}
     */
    static get THIRTY_ONE() {
        return EzNumber._31;
    }
    /**
     * @static
     * @public @readonly @property
     * Gets the number 30.
     * @returns {number}
     */
    static get _31() {
        return 31;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 40
     * @returns {number}
     */
    static get FOURTY() {
        return EzNumber._40;
    }
    /**
     * @static
     * @public @readonly @property
     * Gets the number 40
     * @returns {number}
     */
    static get _40() {
        return 40;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 45.
     * @returns {number}
     */
    static get FOURTY_FIVE() {
        return EzNumber._45;
    }
    /**
     * @static
     * @public @readonly @property
     * Gets the number 45.
     * @returns {number}
     */
    static get _45() {
        return 45;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 59.
     * @returns {number}
     */
    static get FIFTY_NINE() {
        return EzNumber._59;
    }
    /**
     * @static
     * @public @readonly @property
     * Gets the number 59.
     * @returns {number}
     */
    static get _59() {
        return 59;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 60.
     * @returns {number}
     */
    static get SIXTY() {
        return EzNumber._60;
    }
    /**
     * @static
     * @public @readonly @property
     * Gets the number 60.
     * @returns {number}
     */
    static get _60() {
        return 60;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 100.
     * @returns {number}
     */
    static get ONE_HUNDRED() {
        return EzNumber._100;
    }
    /**
     * @static
     * @public @readonly @property
     * Gets the number 100.
     * @returns {number}
     */
    static get _100() {
        return 100;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 400.
     * @returns {number}
     */
    static get _400() {
        return 400;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 500.
     * @returns {number}
     */
    static get _500() {
        return 500;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 999.
     * @returns {number}
     */
    static get NINE_HUNDRED_NINETY_NINE() {
        return EzNumber._999;
    }
    /**
     * @static
     * @public @readonly @property
     * Gets the number 999.
     * @returns {number}
     */
    static get _999() {
        return 999;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 365.
     * @returns {number}
     */
    static get _365() {
        return 365;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the value: 0.00
     * @returns {number}
     */
    static get ZERO$() {
        return 0.00;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the number 365.
     * @returns {number}
     */
    static get THREE_HUNDRED_SIXTY_FIVE() {
        return EzNumber._365;
    }

    /**
     * @static
     * @public @method
     * Wraps the Javascript isNaN function as an alternative method.
     * Returns true if the provided value equals NaN or is not a number, false otherwise.
     * @returns {boolean}
     * @deprecated
     * Migrate to either Javascript's own isNaN(...) method or EzNumber.isNaN(...)
     */
    static isNAN(value) {
        return globalThis.isNaN(value);
    }

    /**
     * @static
     * @public @method
     * Wraps the Javascript isNaN function as an alternative method.
     * Returns true if the provided value equals NaN or is not a number, false otherwise.
     * @returns {boolean}
     * @deprecated
     * Migrate to: globalThis.isNaN(...) (the global Javascript provided function)
     */
    static isNaN(value) {
        return undefined !== value && null !== value && 'number' === typeof value
            ? globalThis.isNaN(value)
            : false;
    }

    /**
     * @static
     * @public @method
     * Determines if the passed value is a of type number.
     * @param {*} value
     * @returns {boolean}
     */
    static isNumber(value) {
        return undefined !== value && null !== value && 'number' === typeof value && !globalThis.isNaN(value);
    }

    /**
     * @static
     * @public @method
     * Returns the provided value if it is a valid number, Otherwise, returns null
     * @param {*} value
     * @returns {number|null}
     */
    static numberOrNull(value) {
        return EzNumber.isNumber(value)
            ? value
            : null;
    }

    /**
     * @static
     * @public @method
     * Transforms the provided value as a string. If the provided value is not a number, 'NaN' is returned.
     * @param {*|number} value
     * @returns {string}
     */
    static asString(value = NaN) {
        return EzNumber.asStringOrDefault(
            value,
            NaN);
    }

    /**
     * @static
     * @public @method
     * Transforms the provided value in one of the following ways:
     * 1) If the provided value equals NaN, then the string 'NaN' is returned.
     * 2) If the provided value is a number, then the number represented as a string is returned.
     * 3) If the provided value is a string with a length, then the value is returned as is.
     * 4) If the provided value is not a number, then the defaultValue is returend.
     * @param {number} aNumber
     * Default: NaN
     * @param {undefined|null|number|string|boolean|*} defaultValue
     * Default: NaN
     * @returns {string}
     */
    static asStringOrDefault(aNumber = NaN, defaultValue = NaN) {
        if (globalThis.isNaN(aNumber)) {
            // If the value is NaN, then return 'NaN'
            return 'NaN';
        }

        if (EzNumber.isNumber(aNumber)) {
            return aNumber.toString();
        }

        if ('string' === typeof aNumber && 0 < aNumber.length) {
            // If the value is already a string, return the value as provided
            return aNumber;
        }

        // Otherwise, return the defaultValue.toString()
        return defaultValue;
    }

    /**
     * @static
     * @public @method
     * Returns the provided value if it is a valid number, Otherwise, returns null
     * @param {*} value
     * @returns {number|null}
     */
    static numberOrNaN(value) {
        return EzNumber.isNumber(value)
            ? value
            : NaN;
    }

    /**
     * @static
     * @public @method
     * Returns the provided value if it is a valid number, Otherwise, returns the provided aDefault value.
     * @param {*} value
     * @param {number|null} aDefault
     * @returns {number|{aDefault}}
     */
    static numberOrDefault(value, aDefault) {
        return EzNumber.isNumber(value)
            ? value
            : aDefault;
    }

    /**
     * @static
     * @public @method
     * Converts a string value to a Javascript number (using parseInt)
     * If the stringValue is null, undefined, or not a number, then NaN is returned.
     * @param {undefined|null|string} aValue
     * @param {undefined|null|number} radix
     * A value of undefined, null, NaN or zero will result in a default radix of 10 (decimal).
     * An integer between 2 and 36 that represents the radix (the base in mathematical numeral systems) of the string.
     * Default: 10
     * @returns {number}
     * @deprecated
     * Migrate to one of the following:
     *  1) EzNumber.asInt(aValue, radix = 10)
     */
    static asNumber(aValue, radix = 10) {
        return EzNumber.asInt(
            aValue,
            NaN,
            radix);
    }

    /**
     * @static
     * @public @method
     * Transforms the provided aValue (assumed a string) to a floating point number using parseFloat.
     * @param {undefined|null|string|number|boolean} aValue
     * Default: 0.0
     * @returns {number}
     */
    static asFloat(aValue = 0.0) {
        return EzNumber.asFloat(aValue, NaN);
    }

    /**
     * @static
     * @public @method
     * Transforms the provided aValue to a floating point number (usin asFloat).
     * If aValue cannot transform to a number, then null is returned.
     * @param {undefined|null|string|number|boolean} aValue
     * Default: 0.0
     * @param {any} defaultValue
     * Default value: NaN
     * @returns {number}
     */
    static asFloatOrNull(aValue = 0.0) {
        return EzNumber.asFloatOrDefault(aValue, null);
    }

    /**
     * @static
     * @public @method
     * Transforms the provided aValue to a number. If aValue cannot transform to a number, then the defaultValue is returned.
     * @param {undefined|null|string|number|boolean} aValue
     * Default: 0.0
     * @param {any} defaultValue
     * Default value: NaN
     * @returns {number}
     */
    static asFloatOrDefault(aValue = 0.0, defaultValue = NaN) {
        if (undefined === aValue || null === aValue) {
            return defaultValue;
        }

        if (EzNumber.isNumber(aValue)) {
            return aValue;
        }

        if ('boolean' === typeof aValue) {
            return aValue
                ? 1.0
                : 0.0;
        }

        if ('string' !== typeof aValue) {
            return defaultValue;
        }

        try {
            return parseFloat(aValue);
        } catch (err) {
            globalThis.window.console.error(
                `Failed to transform value ${aValue} to a floating point number in call to EzNumber.asFloatOrDefault(...): ${err.message}`);

            return defaultValue;
        }
    }

    /**
     * @static
     * @public @method
     * Converts a string value to a Javascript number (using parseInt).
     * If the stringValue is null, undefined, or not a number then the provided defaultValue is returned.
     * @param {undefined|null|string} aValue
     * Default: NaN (returns defaultValue)
     * @param {undefined|null|NaN|number|*} defaultValue
     * Default: NaN
     * @param {undefined|null|number} radix
     * A value of undefined, null, NaN or zero will result in a default radix of 10 (decimal).
     * An integer between 2 and 36 that represents the radix (the base in mathematical numeral systems) of the string.
     * Default: 10
     * @returns {number}
     */
    static asNumberOrDefault(aValue = NaN, defaultValue = NaN, radix = 10) {
        if (EzNumber.isNumber(aValue)) {
            return aValue;
        }

        if (!EzObject.isValid(aValue) || 'string' !== typeof aValue || EzNumber.ZERO === aValue.length) {
            globalThis.console.warn(
                `Returning the provided default value of ${defaultValue} in call to ${EzNumber.asNumberOrDefault.toString()} ` +
                `due to the folowing error: The provided value of ${aValue} is either undefined, null, or cannot transform to a valid number.`);

            // fall to NaN if the string value is null, empty, or undefined
            return defaultValue;
        }

        if (!EzNumber.isNumber(radix)) {
            radix = 10;
        }

        try {
            const numberValue = parseInt(
                aValue,
                radix);

            if (globalThis.isNaN(numberValue)) {
                globalThis.console.warn(
                    `Returning the defaultValue of ${defaultValue} in call to ${EzNumber.asNumberOrDefault.toString()} ` +
                    `due to the folowing error: The provided aValue of ${aValue} cannot transform to a valid number.`);

                // Could not translate aValue into a number, returning the default value
                return defaultValue;
            }

            const parseIntResult = parseInt(
                numberValue,
                radix);

            return globalThis.isNaN(parseIntResult)
                ? defaultValue
                : parseIntResult;
        } catch (err) {
            globalThis.ezApi.ezclocker.ezLogger.debug(
                `Failed to transform tFailed to transform the provided value of ${aValue} to a number ${radix || 10}` +
                `due to the following error: ${err?.message || 'Generic error'}` +
                `Therefore, returning the default value of ${defaultValue}.`,
                err);

            return defaultValue;
        }
    }

    /**
     * @static
     * @public @method
     * Converts a string value to a Javascript number (using parseInt)
     * If the stringValue is null, undefined, or not a number, then NaN is returned.
     * @param {undefined|null|string} aValue
     * @param {undefined|null|number} radix
     * A value of undefined, null, NaN or zero will result in a default radix of 10 (decimal).
     * An integer between 2 and 36 that represents the radix (the base in mathematical numeral systems) of the string.
     * Default: 10
     * @returns {number}
     * Returns the number as an integer
     */
    static asInt(aValue, radix = 10) {
        if (EzNumber.isNumber(aValue)) {
            return aValue;
        }
        if (!EzNumber.isNumber(radix)) {
            radix= 10;
        }

        if (undefined === aValue || null == aValue || 'string' !== typeof aValue || 0 === aValue.length) {
            globalThis?.console?.warn(
                `Returning NaN from ${EzNumber.asNumber.toString()}} ` +
                `due to the folowing error: The provided value of ${aValue} is either undefined, null, or cannot transform to a number with radix ${radix}.`);

            return NaN;
        }

        return EzNumber.asIntOrDefault(
            aValue,
            NaN,
            radix);
    }

    /**
     * @static
     * @public @method
     * Converts a string value to a Javascript integer (using parseInt).
     * If the stringValue is null, undefined, or not a number then null is returned
     * @param {undefined|null|string} aValue
     * Default: null
     * @param {undefined|null|number} radix
     * A value of undefined, null, NaN or zero will result in a default radix of 10 (decimal).
     * An integer between 2 and 36 that represents the radix (the base in mathematical numeral systems) of the string.
     * Default: 10
     * @returns {null|number}
     * Returns the number as an integer
     */
    static asIntOrNull(aValue = null, radix = 10) {
        return EzNumber.asIntOrDefault(aValue, null, radix);
    }

    /**
     * @static
     * @public @method
     * Converts a string value to a Javascript number (using parseInt).
     * If the stringValue is null, undefined, or not a number then the provided defaultValue is returned.
     * @param {undefined|null|string} aValue
     * Default: NaN (returns defaultValue)
     * @param {undefined|null|NaN|number|*} defaultValue
     * Default: NaN
     * @param {undefined|null|number} radix
     * A value of undefined, null, NaN or zero will result in a default radix of 10 (decimal).
     * An integer between 2 and 36 that represents the radix (the base in mathematical numeral systems) of the string.
     * Default: 10
     * @returns {any|number}
     * Returns the number as an integer
     */
    static asIntOrDefault(aValue = NaN, defaultValue = NaN, radix = 10) {
        if (EzNumber.isNumber(aValue)) {
            return aValue;
        }
        if (!EzNumber.isNumber(radix)) {
            radix= 10;
        }

        if (!EzObject.isValid(aValue) || 'string' !== typeof aValue || 0 === aValue.length) {
            globalThis.console.warn(
                `Returning the provided default value of ${defaultValue} in call to ${EzNumber.asNumberOrDefault.toString()}: ` +
                `The provided stringValue of ${aValue} is either undefined, null, or cannot transform to a number with radix ${radix}.`);

            // fall to NaN if the string value is null, empty, or undefined
            return defaultValue;
        }

        try {
            const numberValue = parseInt(aValue, 10);

            if (globalThis.isNaN(numberValue)) {
                globalThis.console.warn(
                    `Returning the provided default value of ${defaultValue} in call to ${EzNumber.asNumberOrDefault.toString()}: ` +
                    `Cannot transform the provided aValue of ${aValue} to a number with radix ${radix}.`);

                // Could not translate aValue into a number, returning the default value
                return defaultValue;
            }

            const parseIntResult = parseInt(
                numberValue,
                radix);

            return globalThis.isNaN(parseIntResult)
                ? defaultValue
                : parseIntResult;
        } catch (err) {
            globalThis.ezApi.ezclocker.ezLogger.debug(
                `Failed to transform tFailed to transform the provided value of ${aValue} to a number with radix ${radix}` +
                `due to the following error: ${err?.message || 'Generic error'}` +
                `Therefore, returning the default value of ${defaultValue}.`,
                err);

            return defaultValue;
        }
    }

    /**
     * @static
     * @public @method
     * Transforms the provided string value into an integer.
     * Returns NaN if the provided string value cannot transform to an integer.
     * @param {string} value
     * @returns {number}
     */
    static strToInt(value) {
        if (EzNumber.isNumber(value)) {
            return value;
        }

        return EzNumber.strToIntDefault(
            value,
            NaN);
    }

    /**
     * @static
     * @public @method
     * Transforms the provided string value into an integer.
     * Returns the provided defaultValue if the provided string value cannot transform to an integer.
     * @param {string} stringValue
     * @param {undefined|null|number|*} defaultValue
     * Can be any value (does not need to be an number)
     * Default: NaN
     * @returns {undefined|null|number|*}
     */
    static strToIntDefault(stringValue, defaultValue = NaN) {
        if (!EzObject.isValid(stringValue) || 'string' !== typeof stringValue || 0 === stringValue.length) {
            globalThis.console.warn(
                `Returning the default value of ${defaultValue} from call to EzNumber.strToIntDefault(...): ` +
                `The provided stringValue of ${stringValue} is either undefined, null, not a string, or a string with zero length.`);

            return defaultValue;
        }

        try {
            const parseIntResult = parseInt(stringValue, 10);

            if (globalThis.isNaN(parseIntResult)) {
                globalThis.console.warn(
                    `Returning the default value of ${defaultValue} from call to EzNumber.strToIntDefault(...): ` +
                    `The provided stringValue of ${stringValue} does not represent a valid integer.`);

                return defaultValue;
            }

            return parseIntResult;
        } catch (err) {
            globalThis.console.warn(
                `Returning the default value of ${defaultValue} from call to EzNumber.strToIntDefault(...) ` +
                `after failing to transform the provided stringValue of ${stringValue} to an integer due to the following error: ${err.message}.`);

            return defaultValue;
        }
    }

    /**
     * @static
     * @public @method
     * Transforms the provided string value into a floating point number by calling EzNumber.strToFloatDefault(stringValue, NaN)
     * @param {string} value
     * Expecting: A floating point number represented as a string.
     * @returns {number}
     */
    static strToFloat(stringValue) {
        if (!EzObject.isValid(stringValue) || 'string' !== typeof stringValue || 0 === stringValue.length) {
            globalThis.console.warn(
                `Returning NaN from call to EzNumber.strToFloat(...): ` +
                `The provided stringValue of ${stringValue} is either undefined, null, not a string, or a string with zero length.`);

            return NaN;
        }

        return EzNumber.strToFloatDefault(stringValue, NaN);
    }

    /**
     * @static
     * @public @method
     * Transforms the provided stringValue into a floating point number.
     * If the stringValue cannot transform to a floating point number then the defaultValue is returned instead.
     * @param {string} value
     * Expecting: A floating point number represented as a string.
     * @param {undefined|null|number|*} defaultValue
     * Can be any value (does not have to be a floating point value)
     * @returns {undefined|null|number|*}
     */
    static strToFloatDefault(stringValue, defaultValue = NaN) {
        if (!EzObject.isValid(stringValue) || 'string' !== typeof stringValue || 0 === stringValue.length) {
            globalThis.console.warn(
                `Returning the provided default value of ${defaultValue} from call to EzNumber.strToFloatDefault(...): ` +
                `The provided stringValue of ${stringValue} is either undefined, null, an empty string, or not a string.`);

            return defaultValue;
        }

        try {
            const parseFloatResult = parseFloat(stringValue);

            if (globalThis.isNaN(parseFloatResult)) {
                globalThis.console.warn(
                    `Returning the provided default value of ${defaultValue} from call to EzNumber.strToFloatDefault(...): ` +
                    `The provided stringValue of ${stringValue} does not transform to a valid floating point number.`);

                return defaultValue;
            }

            return parseFloatResult;
        } catch (err) {
            globalThis.console.warn(
                `Returning the provided default value of ${defaultValue} from call to EzNumber.strToFloatDefault(...) ` +
                `after failing to transform the provided stringValue of ${stringValue} ` +
                `to a floating point number due to the following error: ${err.message}.`);

            return defaultValue;
        }
    }

    /**
     * @static
     * @public @method
     * Determines: value >= aMinNumber && value <= aMaxNumber
     * @param {number} aNumber
     * @param {undefined|null|number} aMinNumber
     * If a number is not provided then aMinNumber is set equal Number.MIN_VALUE.
     * Default: Number.MIN_VALUE
     * @param {undefined|null|number} aMaxNumber
     * If a number is not provided then aMaxNumber is set equal to Number.MAX_VALUE.
     * Default: Number.MAX_VALUE
     * @returns {boolean}
     * Returns true if the provided aNumber is >= aMinNumber OR aNumber <= aMaxNumber.
     * Returns false otherwise.
     */
    static isNumberWithinRange(aNumber, aMinNumber = Number.MIN_VALUE, aMaxNumber = Number.MAX_VALUE) {
        if (!EzNumber.isNumber(aNumber)) {
            throw new EzBadParamException(
                'aNumber',
                EzNumber,
                EzNumber.isNumberWithinRange,
                `The provided value of ${aNumber} is not a valid number.`);
        }

        if (!EzNumber.isNumber(aMinNumber)) {
            aMinNumber = Number.MIN_VALUE;
        }

        if (!EzNumber.isNumber(aMaxNumber)) {
            aMaxNumber = Number.MAX_VALUE;
        }

        if (aMinNumber > aMaxNumber) {
            throw new EzBadParamException(
                'aMinNumber',
                EzNumber,
                EzNumber.isNumberWithinRange,
                'The aMinNumber parameter must be less than or equal to the aMaxNumber parameter.');
        }

        return aNumber >= aMinNumber && aNumber <= aMaxNumber;
    }

    /**
     * @static
     * @public @method
     * Determines if the provided value param is between the aMinNumber and aMaxNumber range.
     * @param {number} aNumber
     * @param {undefined|null|number} aMinNumber
     * If aMinNumber is undefined or null then aMinNumber is set equal to Number.MIN_VALUE
     * Default: Number.MIN_VALUE
     * @param {number} aMaxNumber
     * If a aMaxNumber is undefined or null then aMaxNumber is set equal to Number.MAX_VALUE
     * Default: Number.MAX_VALUE
     * @returns {boolean}
     * 1) If the provided aNumber is not a valid number then an exception is thrown.
     * 2) If aMinNumber is greater than a aMaxNumber then an exception is thrown.
     * 3) Returns true if aNumber is > aMinNumber AND aNumber is < aMaxNumber
     */
    static isNumberBetweenRange(aNumber, aMinNumber = Number.MIN_VALUE, aMaxNumber = Number.MAX_VALUE) {
        if (!EzNumber.isNumber(aNumber)) {
            throw new EzBadParamException(
                'value',
                EzNumber,
                EzNumber.isNumberBetweenRange,
                `The provided value of ${aNumber} is not a valid number.`);
        }

        if (!EzNumber.isNumber(aMinNumber)) {
            aMinNumber = Number.MIN_VALUE;
        }

        if (!EzNumber.isNumber(aMaxNumber)) {
            aMaxNumber = Number.MAX_VALUE;
        }

        if (aMinNumber > aMaxNumber) {
            throw new EzBadParamException(
                'aMinNumber',
                EzNumber,
                EzNumber.isNumberWithinRange,
                'The aMinNumber parameter must be less than or equal to the aMaxNumber parameter.');
        }

        return aNumber > aMinNumber && aNumber < aMaxNumber;
    }

    /**
     * @static
     * @public @method
     * If the provided aNumber is a valid number then the aNumber value is returned.
     * Otherwise, zero is returned.
     * @param {undefined|null|number|*} number
     * @returns {number}
     */
    static numberOrZero(aNumber) {
        return EzNumber.isNumber(aNumber)
            ? aNumber
            : 0;
    }

    /**
     * @static
     * @public @method
     * Returns the provided number as a currency string using the provided
     * EzLocale and EzCurrency values.
     * @param {number} aNumber
     * @param {string} ezLocal
     * A valid enumeration property value from /ezlibrary/locales/EzLocale.js
     * Default: 'en-us'
     * @param {string} ezCurrency
     * A valid enumeration property value from /ezlibrary/locales/EzCurrency.js
     * Default: 'USD'
     */
    static numberAsCurrencyString(aNumber, ezLocale, ezCurrency) {
        if (!EzObject.isValid(ezLocale) || 'string' !== typeof ezLocale) {
            ezLocale = 'en';
        }
        if (!EzObject.isValid(ezCurrency) || 'string' !== typeof ezCurrency) {
            ezCurrency = 'USD';
        }

        // Create our number formatter.
        const numberFormatter = new Intl.NumberFormat(
            ezLocale,
            {
                style: 'currency',
                currency: ezCurrency
            });

        return numberFormatter.format(aNumber);
    }

    /**
     * @static
     * @public @method
     * Formats the provided value to have the provided number of decimal places.
     * References:
     *     https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/toFixed
     * @param {number} aNumber
     * @param {undefined|null|number} numberOfDecimalPlaces
     * Default: 0
     * @returns {number}
     * Returns a floating point number
     */
    static formatDecimal(aNumber, numberOfDecimalPlaces) {
        if (!EzNumber.isNumber(aNumber)) {
            throw new EzBadParamException(
                'aNumber',
                EzNumber,
                EzNumber.formatDecimal);
        }
        if (!EzNumber.isNumber(numberOfDecimalPlaces)) {
            numberOfDecimalPlaces = 0;
        }
        if (0 > numberOfDecimalPlaces || 100 < numberOfDecimalPlaces) {
            throw new EzBadParamException(
                'numberOfDecimalPlaces (must be between 0 and 100)',
                EzNumber,
                EzNumber.formatDecimal);
        }

        return parseFloat(aNumber.toFixed(numberOfDecimalPlaces));
    }

    /**
     * @static
     * @public @method
     * Transforms the provided number into a string padding a zero at the front if
     * the aNumber < 10;
     * @param {undefined|null|number} aNumber
     * Default: null
     * @returns {string}
     */
    static toIso2DigitValue(aNumber = null) {
        if (!EzNumber.isNumber(aNumber)) {
            return '00';
        }

        return aNumber < 10
            ? `0${aNumber}`
            : EzNumber.asString(aNumber);
    }

    /**
     * @static
     * @public @method
     * Transforms the provided number to a two digit iso date string (appending 0 if necesesary)
     * @param {number} aNumber
     * @returns {string}
     */
    static toIsoDateString(aNumber = null) {
        return EzNumber.toIso2DigitValue(aNumber);
    }

    /**
     * @static
     * @public @method
     * Transforms the provided number to a two digit iso month string (appending 0 if necesesary)
     * @param {number} aNumber
     * @returns {string}
     */
    static toIsoMonthString(aNumber = null) {
        return EzNumber.toIso2DigitValue(aNumber);
    }

    /**
     * @static
     * @public @method
     * Transforms the provided number to a two digit iso hours string (appending 0 if necesesary)
     * @param {number} aNumber
     * @returns {string}
     */
    static toIsoHourString(aNumber = null) {
        return EzNumber.toIso2DigitValue(aNumber);
    }

    /**
     * @static
     * @public @method
     * Transforms the provided number to a two digit iso minutes string (appending 0 if necesesary)
     * @param {number} aNumber
     * @returns {string}
     */
    static toIsoMinuteString(aNumber = null) {
        return EzNumber.toIso2DigitValue(aNumber);
    }

    /**
     * @static
     * @public @method
     * Transforms the provided number to a two digit iso seconds string (appending 0 if necesesary)
     * @param {number} aNumber
     * @returns {string}
     */
    static toIsoSecondString(aNumber = null) {
        return EzNumber.toIso2DigitValue(aNumber);
    }

    /**
     * @static
     * @public @method
     * Transforms the provided number to a three digit iso milliseconds string (appending 0 if necesesary)
     * @param {number} aNumber
     * @returns {string}
     */
    static toIsoMillisecondString(aNumber) {
        if (10 > aNumber) {
            return `00${aNumber}`;
        }

        if (100 > aNumber) {
            return `0${aNumber}`;
        }

        return EzNumber.asString(aNumber);
    }
}

globalThis.EzNumber = EzNumber;
